/**
 * EditableToken Component
 * 
 * Wraps individual content tokens to make them selectable in edit mode.
 * This enables granular selection of text elements within a component,
 * rather than only section-level selection.
 * 
 * Usage:
 * ```tsx
 * import { EditableToken } from '@vibesites/runtime/react';
 * 
 * const title = useContentToken('hero.title', { defaultValue: 'Welcome' });
 * 
 * return (
 *   <h1>
 *     <EditableToken path="hero.title">{title}</EditableToken>
 *   </h1>
 * );
 * ```
 */

import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
  type MouseEvent,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { useEditMode, requestInlineTokenEditor, postScopeTargetSelected, postCommentPinTargetSelected, type TokenBounds } from './edit-mode';
import { useGeneratingContent } from './generating-paths';
import { useBlockCatalogEntries, useScopedStyleOverride, useScopedContentOverride, useTokenDefinition } from './context';
import { useSectionEditInfo } from './editable-section';
import { useReportedRuntimeDefinition } from './runtime-definitions';
import type { ContentTokenType } from '../types';

// ============================================================================
// Types
// ============================================================================

export interface EditableTokenProps {
  /** Token path (e.g., 'hero.title', 'hero.cta_text') */
  path: string;
  /** Token type for visual styling hints */
  type?: ContentTokenType;
  /** Display label for the token (shown in tooltip) */
  label?: string;
  /** Token description for the editor */
  description?: string;
  /** Default property field (for field mode) */
  defaultField?: string;
  /** Default value (for custom mode) */
  defaultValue?: string;
  /** For block tokens: restrict which block categories are shown in the picker */
  acceptedBlockCategories?: string[];
  /** Children to render (the token value) */
  children: ReactNode;
  /** Additional class name */
  className?: string;
  /** Additional inline styles */
  style?: CSSProperties;
  /** 
   * HTML tag to render. Defaults to 'span'.
   * Use semantic elements like 'h1', 'h2', 'p' to preserve document structure,
   * or 'div'/'span' for generic containers.
   */
  as?: 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'a' | 'label' | 'li' | 'figcaption';
}

// ============================================================================
// Component
// ============================================================================

/**
 * EditableToken wraps a content token value to make it selectable in edit mode.
 * 
 * Features:
 * - Shows subtle hover outline when edit mode is active
 * - Displays token label on hover
 * - Click opens inline editor (if enabled) or selects token for sidebar editing
 * - Does not interfere with normal rendering when edit mode is off
 * - Gets save callbacks from VibesitesContext (no props needed from theme components)
 */
export function EditableToken({
  path,
  type: typeProp,
  label: labelProp,
  description: descriptionProp,
  defaultField: defaultFieldProp,
  defaultValue: defaultValueProp,
  acceptedBlockCategories,
  children,
  className = '',
  style,
  as: Tag = 'span',
}: EditableTokenProps) {
  const editMode = useEditMode();
  const sectionEditInfo = useSectionEditInfo();
  const tokenRef = useRef<HTMLElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const blockCatalogEntries = useBlockCatalogEntries();
  const styleOverride = useScopedStyleOverride(path);
  const override = useScopedContentOverride(path);

  // Resolve effective props from contentSchema when not explicitly provided
  const schemaDef = useTokenDefinition(path);
  const type = typeProp ?? (schemaDef?.type as EditableTokenProps['type']) ?? 'text';
  const label = labelProp ?? schemaDef?.label;
  const description = descriptionProp ?? schemaDef?.description;
  const defaultField = defaultFieldProp ?? schemaDef?.defaultField;
  const defaultValue = defaultValueProp ?? schemaDef?.defaultValue;

  // Generating SHIMMER (editor-preview only): wraps `children` while this field
  // is actively generating. Off the editor it's a pure pass-through.
  const content = useGeneratingContent(path, children, 'span');

  // Get edit mode state (gracefully handle when not in edit mode context)
  const isEditMode = editMode?.isEditMode ?? false;
  const selectedToken = editMode?.selectedToken;
  const isSelected = selectedToken === path;
  const mode: 'field' | 'custom' = override?.overrideType === 'VALUE' 
    ? 'custom' 
    : (override?.overrideType === 'FIELD_MAPPING' || defaultField) 
      ? 'field' 
      : 'custom';
  const currentField = override?.overrideType === 'FIELD_MAPPING' 
    ? override.mappedField 
    : defaultField || null;
  const currentValue = override?.overrideType === 'VALUE' 
    ? override.customValue 
    : defaultValue || null;
  
  // Build token definition for the cross-frame editor overlay
  const definition = useMemo(() => ({
    label: label || path.split('.').pop() || path,
    type,
    description,
    defaultField,
    defaultValue,
    // For block tokens, include catalog entries and accepted categories for the inline picker
    ...(type === 'block' ? { blockCatalogEntries, acceptedBlockCategories } : {}),
  }), [label, path, type, description, defaultField, defaultValue, blockCatalogEntries, acceptedBlockCategories]);

  useReportedRuntimeDefinition('token', path, definition, isEditMode);

  // Handle mouse enter
  const handleMouseEnter = useCallback((e: MouseEvent) => {
    if (!isEditMode) return;
    // Stop propagation to prevent parent EditableSection from showing hover
    e.stopPropagation();
    setIsHovered(true);
    editMode?.setHoveredToken(path);
  }, [isEditMode, editMode, path]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    if (!isEditMode) return;
    setIsHovered(false);
    editMode?.setHoveredToken(null);
  }, [isEditMode, editMode]);

  // Dismiss hover portal on scroll so fixed-position labels don't drift
  useEffect(() => {
    if (!isEditMode || !isHovered) return;
    const dismiss = () => {
      setIsHovered(false);
      editMode?.setHoveredToken(null);
    };
    window.addEventListener('scroll', dismiss, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', dismiss, { capture: true });
  }, [isEditMode, isHovered, editMode]);

  const handleSelect = useCallback(() => {
    if (!isEditMode || !editMode) return;

    const rect = tokenRef.current?.getBoundingClientRect();
    const bounds: TokenBounds | undefined = rect ? {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      bottom: rect.bottom,
      right: rect.right,
    } : undefined;

    // Scope mode routes the click to the parent's AI composer as a token chip.
    if (editMode.scopeMode) {
      postScopeTargetSelected({
        kind: 'token',
        componentInfo: sectionEditInfo,
        tokenPath: path,
        tokenType: type,
        // For block tokens, this is the live blockId (covers DB-stored VALUE
        // overrides). FIELD_MAPPING or unset → null, handled by the parent.
        tokenValue: typeof currentValue === 'string' ? currentValue : null,
      });
      return;
    }
    if (editMode.commentPinMode) {
      postCommentPinTargetSelected({
        kind: 'token',
        componentInfo: sectionEditInfo,
        tokenPath: path,
        tokenType: type,
        tokenValue: typeof currentValue === 'string' ? currentValue : null,
      });
      return;
    }

    if (!bounds) return;
    editMode.startInlineEdit(path, bounds);
    requestInlineTokenEditor({
      path,
      definition,
      currentValue,
      mode,
      currentField,
      anchorBounds: bounds,
      componentInfo: sectionEditInfo,
    });
  }, [isEditMode, editMode, path, definition, currentValue, mode, currentField, sectionEditInfo]);

  const handleClickCapture = useCallback((e: MouseEvent) => {
    if (!isEditMode || !editMode) return;
    e.preventDefault();
    e.stopPropagation();
    (e.nativeEvent as Event & { stopImmediatePropagation?: () => void }).stopImmediatePropagation?.();
    handleSelect();
  }, [isEditMode, editMode, handleSelect]);

  // Merge theme-provided style with any style overrides from the database
  const mergedStyle = styleOverride
    ? { ...style, ...styleOverride.properties } as CSSProperties
    : style;

  // Always render wrapper when as is explicitly set (e.g. as="h2", as="div")
  // Previously,even if we defined a header tag or other style, it would not render the tag if no additonal styling was passed.
  // This was also an issue with Images, as they would lose their <div> separator in edit mode, breaking the image layout entirely.
  const hasExplicitTag = Tag !== 'span';

  // Tag the wrapper with the token path when a styleOverride exists, so runtime
  // CSS rules (e.g. link-color rules) can scope to a specific token in production.
  const tokenPathAttr = styleOverride ? { 'data-vibesite-token': path } : undefined;

  // If not in edit mode context, just render children directly
  if (!editMode) {
    if (mergedStyle || className || hasExplicitTag) {
      return <Tag className={className} style={mergedStyle} {...tokenPathAttr}>{children}</Tag>;
    }
    return <>{children}</>;
  }

  // In production (non-edit mode), render without wrapper overhead. The preview
  // frame can have edit mode toggled off while a field is still generating, so
  // `content` (skeleton or children) is used here too.
  if (!isEditMode) {
    if (mergedStyle || className || hasExplicitTag) {
      return <Tag className={className} style={mergedStyle} {...tokenPathAttr}>{content}</Tag>;
    }
    return <>{content}</>;
  }

  // Derive display label from path if not provided
  const displayLabel = label || path.split('.').pop() || path;

  // Compute fixed-position coords for portaled tooltips from the element's rect
  const rect = (isHovered || isSelected) ? tokenRef.current?.getBoundingClientRect() : null;

  return (
    <>
      <Tag
        ref={tokenRef as React.RefObject<never>}
        className={`vibesite-editable-token ${className}`}
        data-vibesite-token={path}
        data-vibesite-token-type={type}
        data-vibesite-token-hovered={isHovered}
        data-vibesite-token-selected={isSelected}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClickCapture={handleClickCapture}
        style={{
          cursor: 'pointer',
          ...style,
          ...styleOverride?.properties,
        }}
      >
        {content}

        {/* Inline styles for token states */}
        <style>{editableTokenStyles}</style>
      </Tag>

      {/* Portaled tooltip label — rendered outside the token to avoid position/style interference */}
      {isHovered && !isSelected && rect && createPortal(
        <span
          className="vibesite-token-label"
          style={{
            position: 'fixed',
            top: rect.top - 4,
            left: rect.left + rect.width / 2,
            transform: 'translate(-50%, -100%)',
            background: '#773BFF',
            color: '#fff',
            fontSize: '10px',
            fontFamily: 'ui-sans-serif, system-ui, sans-serif',
            fontWeight: 500,
            padding: '2px 8px',
            borderRadius: '4px',
            whiteSpace: 'nowrap',
            zIndex: 10001,
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          {displayLabel}
        </span>,
        document.body,
      )}

      {/* Portaled selected indicator */}
      {isSelected && rect && createPortal(
        <span
          className="vibesite-token-selected-label"
          style={{
            position: 'fixed',
            top: rect.top - 4,
            left: rect.left + rect.width / 2,
            transform: 'translate(-50%, -100%)',
            background: '#773BFF',
            color: '#fff',
            fontSize: '10px',
            fontFamily: 'ui-sans-serif, system-ui, sans-serif',
            fontWeight: 600,
            padding: '3px 10px',
            borderRadius: '4px',
            whiteSpace: 'nowrap',
            zIndex: 10001,
            pointerEvents: 'none',
            boxShadow: '0 2px 12px rgba(119, 59, 255, 0.4)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <svg 
            width="10" 
            height="10" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          Editing: {displayLabel}
        </span>,
        document.body,
      )}
    </>
  );
}

// ============================================================================
// Styles
// ============================================================================

const editableTokenStyles = `
/* Hover state */
.vibesite-editable-token[data-vibesite-token-hovered="true"] {
  outline: 1px dashed rgba(119, 59, 255, 0.7) !important;
  outline-offset: 2px !important;
  border-radius: 2px;
}

/* Selected state */
.vibesite-editable-token[data-vibesite-token-selected="true"] {
  outline: 2px solid #773BFF !important;
  outline-offset: 3px !important;
  border-radius: 2px;
  background: rgba(119, 59, 255, 0.08);
}

/* Prevent text selection on click */
.vibesite-editable-token {
  user-select: none;
}

/* Ensure token is above section outline */
.vibesite-editable-token[data-vibesite-token-hovered="true"],
.vibesite-editable-token[data-vibesite-token-selected="true"] {
  z-index: 1;
}
`;

export default EditableToken;
